import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { FileText, Search, Eye, Trash2, Printer, CheckCircle2, Wallet, Pencil, Plus, Receipt, RotateCcw } from 'lucide-react';
import { useOrgId } from '../../hooks/useOrgId';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import amountToWords from '../../utils/amountInWords';

const METHOD_LABELS = { cash: 'Cash', bank_transfer: 'Bank Transfer', cheque: 'Cheque' };
const ALLOC_STATUS = { full: 'bg-emerald-100 text-emerald-700', partial: 'bg-amber-100 text-amber-700', none: 'bg-muted text-muted-foreground' };

const lineSub = (l) => (l.sub_total != null ? Number(l.sub_total) : (Number(l.amount || 0) - Number(l.waiver || 0)));
const lineBal = (l) => (l.balance != null ? Number(l.balance) : (lineSub(l) - Number(l.paid || 0)));
const dueKey = (l) => l.due_date || '9999-12-31';

const STATUS_COLORS = { unpaid: 'bg-red-100 text-red-700', partial: 'bg-amber-100 text-amber-700', paid: 'bg-emerald-100 text-emerald-700' };

export default function FeesInvoicesPage() {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentOrg, user, checkActionPermission } = useAuth();
  const canEditInv = checkActionPermission('/school/fees/invoices', 'edit');
  const canDeleteInv = checkActionPermission('/school/fees/invoices', 'delete');
  const isSuper = user?.role === 'super_admin';
  const { selectedYearId } = useAcademicYear();
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ class_id: '', status: '', search: searchParams.get('search') || '', guardian_id: '' });
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [view, setView] = useState(null);
  const printRef = useRef(null);
  const [receiptFor, setReceiptFor] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [rForm, setRForm] = useState({ payment_date: new Date().toISOString().slice(0, 10), amount: '', method: 'cash', reference: '', notes: '', description: '' });
  const [included, setIncluded] = useState({});
  const [descTouched, setDescTouched] = useState(false);
  const [editInv, setEditInv] = useState(null);
  const [editLines, setEditLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [printReceipt, setPrintReceipt] = useState(null);
  const receiptPrintRef = useRef(null);
  const [unpostRcp, setUnpostRcp] = useState(null);
  const [unpostRcpReason, setUnpostRcpReason] = useState('');
  const [unpostInv, setUnpostInv] = useState(null);
  const [unpostReason, setUnpostReason] = useState('');

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const params = { academic_year_id: selectedYearId, page, page_size: pageSize };
      if (filters.class_id) params.class_id = filters.class_id;
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      if (filters.guardian_id) params.guardian_id = filters.guardian_id;
      const res = await schoolApi.listFeeInvoices(orgId, params);
      setRows(res.items || []);
      setTotal(res.total || 0);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, [orgId, selectedYearId, filters, page, pageSize]);
  useEffect(() => { load(); }, [load]);
  // Debounce the search box so typing queries the server ~400ms after the last keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.search === searchInput ? f : { ...f, search: searchInput }));
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);
  // Reset to the first page whenever the academic year changes.
  useEffect(() => { setPage(1); }, [selectedYearId]);
  
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  
  // Clamp the page if a deletion/filter shrinks the result set below the current page.
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);
  useEffect(() => { if (orgId) schoolApi.listClasses(orgId, selectedYearId).then(setClasses).catch(() => {}); }, [orgId, selectedYearId]);
  useEffect(() => { if (orgId) schoolApi.financeGuardians(orgId).then((res) => setFamilies(Array.isArray(res) ? res : [])).catch(() => {}); }, [orgId]);
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);
  const setFilter = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); };
  const changePageSize = (n) => { setPageSize(n); setPage(1); };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}?`)) return;
    try { await schoolApi.deleteFeeInvoice(inv.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const printPdf = async () => {
    if (!printRef.current) return;
    const html2pdf = (await import('html2pdf.js')).default;
    await html2pdf().set({
      margin: 10, filename: `${view.invoice_number}.pdf`,
      html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(printRef.current).save();
  };

  const money = (v) => (Number(v) || 0).toLocaleString();

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // Build a standalone, self-paginating A4 print document for the receipt.
  // Each printed page is an independent flex-column block: header on top, a body that
  // grows (flex:1), and a footer pinned to the bottom (margin-top:auto). A small
  // pagination script distributes the content across pages so the school header and
  // the DGCI footer appear on EVERY page and no content is ever truncated.
  const buildReceiptPrintDoc = () => {
    const lines = receiptFor.lines || [];
    const curTime = printReceipt.created_at || '';
    const prior = receipts
      .filter((r) => (r.created_at || '') < curTime)
      .sort((a, b) => ((a.created_at || '') < (b.created_at || '') ? -1 : 1));
    const previouslyPaid = prior.reduce((s, r) => s + Number(r.amount || 0), 0);
    const total = Number(receiptFor.total_subtotal || 0);
    const currentAmt = Number(printReceipt.amount || 0);
    const balanceAfter = Math.max(total - previouslyPaid - currentAmt, 0);
    const upTo = receipts.filter((r) => (r.created_at || '') <= curTime);
    const paidByLine = {};
    upTo.forEach((r) => (r.allocations || []).forEach((a) => { paidByLine[a.line_id] = (paidByLine[a.line_id] || 0) + Number(a.amount || 0); }));
    const curAlloc = Object.fromEntries((printReceipt.allocations || []).map((a) => [a.line_id, Number(a.amount || 0)]));
    const totalFees = lines.reduce((s, l) => s + Number(l.amount || 0), 0);
    const totalWaivers = lines.reduce((s, l) => s + Number(l.waiver || 0), 0);

    const backend = process.env.REACT_APP_BACKEND_URL || '';
    const rawLogo = currentOrg?.logo_url || '';
    const logo = rawLogo ? (rawLogo.startsWith('http') ? rawLogo : `${backend}${rawLogo}`) : '';
    const schoolName = esc(currentOrg?.name || 'School');
    const addr = [currentOrg?.address, currentOrg?.city, currentOrg?.phone, currentOrg?.email]
      .filter(Boolean).map(esc).join(' &middot; ');

    const feeThead = `<tr>
      <th class="l">SL</th><th class="l">Fee</th><th class="l">Due</th>
      <th class="r">Amount</th><th class="r">Waiver</th><th class="r">Subtotal</th>
      <th class="r">This payment</th><th class="r">Paid to date</th><th class="r">Balance</th><th class="c">Status</th>
    </tr>`;
    const feeRows = lines.map((l, i) => {
      const sub = Number(l.sub_total != null ? l.sub_total : (Number(l.amount || 0) - Number(l.waiver || 0)));
      const paid = Number(paidByLine[l.line_id] || 0);
      const rem = Math.max(sub - paid, 0);
      const status = paid <= 0 ? 'Unpaid' : (paid >= sub - 0.01 ? 'Paid' : 'Partial');
      const bg = status === 'Paid' ? '#dcfce7' : status === 'Partial' ? '#ffedd5' : 'transparent';
      const badge = status === 'Paid' ? '#166534' : status === 'Partial' ? '#9a3412' : '#64748b';
      const applied = curAlloc[l.line_id] || 0;
      return `<tr style="background:${bg}">
        <td class="l">${i + 1}</td>
        <td class="l">${esc(l.name)}</td>
        <td class="l">${esc(l.due_date || '\u2014')}</td>
        <td class="r">${money(l.amount)}</td>
        <td class="r">${money(l.waiver)}</td>
        <td class="r">${money(sub)}</td>
        <td class="r" style="color:${applied ? '#166534' : '#94a3b8'};font-weight:${applied ? 700 : 400}">${applied ? money(applied) : '\u2014'}</td>
        <td class="r">${money(paid)}</td>
        <td class="r" style="font-weight:600">${money(rem)}</td>
        <td class="c" style="color:${badge};font-weight:700">${status}</td>
      </tr>`;
    });

    const prevThead = `<tr><th class="l">Receipt</th><th class="l">Date</th><th class="l">Method</th><th class="r">Amount</th></tr>`;
    const prevRows = prior.map((r) => `<tr>
      <td class="l" style="font-family:monospace">${esc(r.receipt_number)}</td>
      <td class="l">${esc(r.payment_date)}</td>
      <td class="l">${esc(METHOD_LABELS[r.method] || r.method)}</td>
      <td class="r">${money(r.amount)}</td>
    </tr>`);
    const prevEmpty = `<tr><td colspan="4" style="color:#777">No previous payments on this invoice.</td></tr>`;

    const studentBox = `<div class="student">
      <div class="nm">${esc(receiptFor.student_name)}</div>
      <div style="font-size:12px;color:#334155;margin-top:2px">Guardian: <strong>${esc(receiptFor.guardian_name || '\u2014')}</strong></div>
      <div class="sgrid">
        <div>Admission #: <strong>${esc(receiptFor.admission_no || '\u2014')}</strong></div>
        <div>Academic Year: <strong>${esc(receiptFor.academic_year_name || '\u2014')}</strong></div>
        <div>Class: <strong>${esc(receiptFor.class_name || '\u2014')}</strong></div>
        <div>Section: <strong>${esc(receiptFor.section_name || '\u2014')}</strong></div>
      </div>
    </div>`;

    const currentPay = `<div class="blk">
      <h4 style="color:#166534">Current Payment</h4>
      <table><tbody><tr class="cur-pay">
        <td class="l" style="font-family:monospace">${esc(printReceipt.receipt_number)}</td>
        <td class="l">${esc(printReceipt.payment_date)}</td>
        <td class="l">${esc((METHOD_LABELS[printReceipt.method] || printReceipt.method) + (printReceipt.reference ? ` \u00b7 Ref ${printReceipt.reference}` : ''))}</td>
        <td class="r" style="font-weight:700">${money(currentAmt)} FCFA</td>
      </tr></tbody></table>
    </div>`;

    const totalsBlk = `<div class="blk"><div class="totals">
      <div class="trow"><span>Total invoiced</span><strong>${money(total)}</strong></div>
      <div class="trow"><span>Total previously paid</span><strong>${money(previouslyPaid)}</strong></div>
      <div class="trow"><span>Current payment</span><strong style="color:#166534">${money(currentAmt)}</strong></div>
      <div class="trow tend"><span>Balance remaining after this receipt</span><strong>${money(balanceAfter)}</strong></div>
    </div></div>`;

    const wordsBlk = `<div class="blk">
      <div class="words">Amount in words: <strong>${esc(amountToWords(currentAmt, 'XOF'))}</strong></div>
      ${printReceipt.description ? `<div style="margin-top:8px;font-size:11px;color:#333">${esc(printReceipt.description)}</div>` : ''}
      ${printReceipt.notes ? `<div style="margin-top:4px;font-size:11px;color:#555">Notes: ${esc(printReceipt.notes)}</div>` : ''}
    </div>`;

    const summaryBlk = `<div class="blk"><div class="summary">
      <div style="font-size:12px;font-weight:800;margin-bottom:8px;text-transform:uppercase;letter-spacing:.5px">Invoice Summary</div>
      <div class="sumgrid">
        <div class="srow"><span>Total Fees</span><strong>${money(totalFees)}</strong></div>
        <div class="srow"><span>Total Waivers</span><strong>${money(totalWaivers)}</strong></div>
        <div class="srow"><span>Net Due</span><strong>${money(total)}</strong></div>
        <div class="srow"><span>Total Paid (all receipts)</span><strong style="color:#166534">${money(receiptFor.total_paid)}</strong></div>
        <div class="srow full"><span style="font-weight:700">Outstanding Balance</span><strong style="color:${Number(receiptFor.balance) > 0 ? '#9a3412' : '#166534'}">${money(receiptFor.balance)} FCFA</strong></div>
      </div>
    </div></div>`;

    const headerHtml = `<div class="hdrow">
      ${logo ? `<img class="logo" src="${esc(logo)}" alt="logo" crossorigin="anonymous"/>` : ''}
      <div class="hdname">
        <div class="t">${schoolName}</div>
        ${addr ? `<div class="s">${addr}</div>` : ''}
        <div class="s up">Payment Receipt</div>
      </div>
      <div class="hdright">
        <div class="num">${esc(printReceipt.receipt_number)}</div>
        <div>Date: ${esc(printReceipt.payment_date)}</div>
        ${receiptFor.academic_year_name ? `<div>Ann&eacute;e scolaire: ${esc(receiptFor.academic_year_name)}</div>` : ''}
        <div>Invoice: <strong>${esc(receiptFor.invoice_number)}</strong></div>
      </div>
    </div>`;

    const footerHtml = `<div class="dgci">Credenciamento na DGCI n&deg;000004A/2022 -- Autoriza&ccedil;&atilde;o da n&deg;00122A/2022 -- Nif:510283209</div>
      <div class="sign"><span>Received by: ____________________</span><span>Signature &amp; Stamp: ____________________</span></div>
      <div class="pageno"></div>`;

    const DOC = {
      header: headerHtml,
      footer: footerHtml,
      blocksBefore: [studentBox],
      fee: { heading: 'Fee Lines', thead: feeThead, rows: feeRows },
      prev: { heading: 'Previous Payments', thead: prevThead, rows: prevRows.length ? prevRows : [prevEmpty] },
      blocksAfter: [currentPay, totalsBlk, wordsBlk, summaryBlk],
    };

    const css = `
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      html, body { margin: 0; padding: 0; background: #fff; }
      body { font-family: 'Manrope', system-ui, Arial, sans-serif; color: #111; font-size: 12px; }
      @page { size: A4 portrait; margin: 0; }
      .page { width: 210mm; height: 297mm; padding: 12mm; display: flex; flex-direction: column; background: #fff; overflow: hidden; }
      .page + .page { page-break-before: always; }
      .hd { flex: 0 0 auto; border-bottom: 2px solid #15317e; padding-bottom: 8px; }
      .bd { flex: 1 1 auto; overflow: hidden; padding-top: 10px; }
      .ft { flex: 0 0 auto; margin-top: auto; border-top: 2px solid #b8860b; padding-top: 6px; text-align: center; }
      .hdrow { display: flex; align-items: center; gap: 12px; }
      .hdrow .logo { width: 54px; height: 54px; object-fit: contain; }
      .hdname .t { font-weight: 800; font-size: 19px; line-height: 1.1; color: #15317e; }
      .hdname .s { font-size: 10px; color: #555; }
      .hdname .up { letter-spacing: 1px; text-transform: uppercase; }
      .hdright { margin-left: auto; text-align: right; font-size: 11px; }
      .hdright .num { font-size: 15px; font-weight: 800; color: #15317e; }
      .ft .dgci { color: #15317e; font-size: 11px; font-weight: 600; }
      .ft .sign { display: flex; justify-content: space-between; font-size: 10px; color: #555; margin-top: 8px; }
      .ft .pageno { font-size: 9px; color: #64748b; margin-top: 4px; }
      h4 { margin: 0 0 5px; font-size: 13px; }
      .tbl { margin-bottom: 4px; }
      .blk { margin-top: 12px; }
      table { width: 100%; border-collapse: collapse; font-size: 11px; }
      thead th { text-align: left; color: #555; border-bottom: 1px solid #ccc; padding: 5px 6px; font-weight: 700; }
      td { padding: 5px 6px; border-bottom: 1px solid #eee; }
      td.r, th.r { text-align: right; } td.c, th.c { text-align: center; } td.l, th.l { text-align: left; }
      .student { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; }
      .student .nm { font-size: 20px; font-weight: 800; line-height: 1.1; }
      .student .sgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 24px; font-size: 12px; margin-top: 8px; }
      .totals { margin-left: auto; width: 320px; font-size: 12px; }
      .totals .trow { display: flex; justify-content: space-between; padding: 2px 0; }
      .totals .tend { border-top: 1px solid #cbd5e1; margin-top: 2px; padding-top: 4px; }
      .summary { border: 2px solid #15317e; border-radius: 8px; padding: 12px 14px; background: #f8fafc; }
      .summary .sumgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; font-size: 12px; }
      .summary .srow { display: flex; justify-content: space-between; }
      .summary .srow.full { grid-column: 1 / -1; border-top: 1px solid #cbd5e1; padding-top: 6px; margin-top: 2px; font-size: 14px; }
      .words { font-size: 11px; font-style: italic; color: #333; }
      .cur-pay { background: #f0fdf4; }
    `;

    const script = `
      (function(){
        var DOC = ${JSON.stringify(DOC)};
        function run(){
          var pagesEl = document.getElementById('pages');
          function makePage(){
            var p = document.createElement('div'); p.className = 'page';
            p.innerHTML = '<div class="hd">' + DOC.header + '</div><div class="bd"></div><div class="ft">' + DOC.footer + '</div>';
            pagesEl.appendChild(p);
            return p.querySelector('.bd');
          }
          var bd = makePage();
          function fits(){ return bd.scrollHeight <= bd.clientHeight + 1; }
          function addBlock(html){
            var tmp = document.createElement('div'); tmp.innerHTML = html;
            var node = tmp.firstElementChild || tmp;
            bd.appendChild(node);
            if (!fits() && bd.children.length > 1){ bd.removeChild(node); bd = makePage(); bd.appendChild(node); }
          }
          function addTable(seg){
            var rows = seg.rows.slice(); var first = true;
            while (rows.length){
              var wrap = document.createElement('div'); wrap.className = 'tbl';
              wrap.innerHTML = '<h4>' + seg.heading + (first ? '' : ' (cont.)') + '</h4><table><thead>' + seg.thead + '</thead><tbody></tbody></table>';
              bd.appendChild(wrap);
              if (!fits() && bd.children.length > 1){ bd.removeChild(wrap); bd = makePage(); bd.appendChild(wrap); }
              var tb = wrap.querySelector('tbody');
              var placed = 0;
              while (rows.length){
                var tpl = document.createElement('template'); tpl.innerHTML = String(rows[0]).trim();
                var tr = tpl.content.firstElementChild;
                tb.appendChild(tr);
                if (!fits()){
                  tb.removeChild(tr);
                  if (placed === 0){
                    if (bd.children.length > 1){ bd.removeChild(wrap); bd = makePage(); bd.appendChild(wrap); tb = wrap.querySelector('tbody'); tb.appendChild(tr); rows.shift(); placed++; continue; }
                    tb.appendChild(tr); rows.shift(); placed++; continue;
                  }
                  break;
                }
                rows.shift(); placed++;
              }
              first = false;
              if (rows.length){ bd = makePage(); }
            }
          }
          DOC.blocksBefore.forEach(addBlock);
          addTable(DOC.fee);
          addTable(DOC.prev);
          DOC.blocksAfter.forEach(addBlock);
          var pages = pagesEl.querySelectorAll('.page');
          for (var i = 0; i < pages.length; i++){
            var pn = pages[i].querySelector('.pageno');
            if (pn) pn.textContent = 'Page ' + (i + 1) + ' of ' + pages.length;
          }
          setTimeout(function(){ try { window.focus(); window.print(); } catch(e){} }, 200);
        }
        if (document.readyState === 'complete') run();
        else window.addEventListener('load', run);
      })();
    `;

    return '<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Receipt '
      + esc(printReceipt.receipt_number) + '</title><style>' + css + '</style></head><body>'
      + '<div id="pages"></div><scr' + 'ipt>' + script + '</scr' + 'ipt></body></html>';
  };

  const printReceiptNative = () => {
    if (!printReceipt || !receiptFor) return;
    const html = buildReceiptPrintDoc();
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup blocked. Please allow popups to print.'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };



  const postInvoice = async (inv) => {
    if (!window.confirm(`Post invoice ${inv.invoice_number} to accounting? This locks it from editing.`)) return;
    try {
      const res = await schoolApi.postFeeInvoice(inv.id);
      toast.success(`Posted \u2014 journal ${res.journal_voucher_number}${res.waiver_voucher_number ? ` (+ waiver ${res.waiver_voucher_number})` : ''}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to post'); }
  };

  const doUnpost = async () => {
    if (!unpostReason.trim()) { toast.error('Please provide a reason'); return; }
    setBusy(true);
    try {
      const res = await schoolApi.unpostFeeInvoice(unpostInv.id, unpostReason.trim());
      const voided = (res.reversed_vouchers || []).join(', ');
      toast.success(`Invoice unposted \u2014 returned to Draft${voided ? ` (voided ${voided})` : ''}`);
      setUnpostInv(null); setUnpostReason('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to unpost'); }
    finally { setBusy(false); }
  };

  const refreshReceiptFor = async (invId) => {
    try {
      const inv = await schoolApi.getFeeInvoice(invId);
      setReceiptFor(inv);
      setIncluded(Object.fromEntries((inv.lines || []).filter((l) => lineBal(l) > 0).map((l) => [l.line_id, true])));
    } catch { /* keep existing */ }
  };

  const openReceipts = async (inv) => {
    setReceiptFor(inv);
    setRForm({ payment_date: new Date().toISOString().slice(0, 10), amount: '', method: 'cash', reference: '', notes: '', description: '' });
    setDescTouched(false);
    setIncluded(Object.fromEntries((inv.lines || []).filter((l) => lineBal(l) > 0).map((l) => [l.line_id, true])));
    try { setReceipts(await schoolApi.listReceipts(orgId, { invoice_id: inv.id })); } catch { setReceipts([]); }
    refreshReceiptFor(inv.id);
  };
  const addReceipt = async () => {
    if (!(Number(rForm.amount) > 0)) { toast.error('Enter a valid amount'); return; }
    const allocations = Object.entries(allocation.alloc).map(([line_id, amount]) => ({ line_id, amount }));
    if (allocations.length === 0) { toast.error('This amount does not cover any selected fee line'); return; }
    setBusy(true);
    try {
      await schoolApi.createReceipt({
        organization_id: orgId, invoice_id: receiptFor.id,
        payment_date: rForm.payment_date, method: rForm.method, reference: rForm.reference, notes: rForm.notes,
        description: rForm.description, amount: Number(rForm.amount), allocations,
      });
      toast.success('Receipt registered');
      setReceipts(await schoolApi.listReceipts(orgId, { invoice_id: receiptFor.id }));
      setRForm({ payment_date: new Date().toISOString().slice(0, 10), amount: '', method: 'cash', reference: '', notes: '', description: '' });
      setDescTouched(false);
      await refreshReceiptFor(receiptFor.id);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  const postReceipt = async (r) => {
    try {
      const res = await schoolApi.postReceipt(r.id);
      toast.success(`Posted \u2014 journal ${res.journal_voucher_number}`);
      setReceipts(await schoolApi.listReceipts(orgId, { invoice_id: receiptFor.id }));
      await refreshReceiptFor(receiptFor.id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const doUnpostReceipt = async () => {
    if (!unpostRcpReason.trim()) { toast.error('Please provide a reason'); return; }
    setBusy(true);
    try {
      const res = await schoolApi.unpostReceipt(unpostRcp.id, unpostRcpReason.trim());
      toast.success(`Receipt unposted${res.reversed_voucher ? ` (reversed ${res.reversed_voucher})` : ''} \u2014 back to Draft`);
      setUnpostRcp(null); setUnpostRcpReason('');
      setReceipts(await schoolApi.listReceipts(orgId, { invoice_id: receiptFor.id }));
      await refreshReceiptFor(receiptFor.id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to unpost'); }
    finally { setBusy(false); }
  };
  const delReceipt = async (r) => {
    if (!window.confirm('Delete this receipt?')) return;
    try { await schoolApi.deleteReceipt(r.id); setReceipts(await schoolApi.listReceipts(orgId, { invoice_id: receiptFor.id })); await refreshReceiptFor(receiptFor.id); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  // ---- Receipt allocation (FIFO by earliest due date, over included lines) ----
  const payableLines = useMemo(
    () => (receiptFor?.lines || []).filter((l) => lineBal(l) > 0).slice().sort((a, b) => (dueKey(a) < dueKey(b) ? -1 : 1)),
    [receiptFor]
  );
  const totalOutstanding = useMemo(() => payableLines.reduce((s, l) => s + lineBal(l), 0), [payableLines]);
  const allocation = useMemo(() => {
    let pool = Math.round(Number(rForm.amount) || 0);
    const alloc = {};
    for (const l of payableLines) {
      if (!included[l.line_id]) continue;
      if (pool <= 0) break;
      const take = Math.min(pool, Math.round(lineBal(l)));
      if (take > 0) { alloc[l.line_id] = take; pool -= take; }
    }
    return { alloc, unallocated: Math.max(pool, 0) };
  }, [rForm.amount, included, payableLines]);

  const autoDescription = useMemo(() => {
    const parts = [];
    for (const l of payableLines) {
      const a = allocation.alloc[l.line_id] || 0;
      if (a <= 0) continue;
      const sub = Math.round(lineSub(l));
      parts.push(a >= sub ? `${l.name} (full)` : `${l.name} (partial \u2014 ${a.toLocaleString()} of ${sub.toLocaleString()})`);
    }
    return parts.length ? `Payment covers: ${parts.join(', ')}` : '';
  }, [payableLines, allocation]);

  useEffect(() => {
    if (!descTouched) setRForm((f) => ({ ...f, description: autoDescription }));
  }, [autoDescription, descTouched]);

  const setEL = (i, k, v) => setEditLines((p) => p.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const openEdit = (inv) => { setEditInv(inv); setEditLines((inv.lines || []).map((l) => ({ ...l }))); };
  const saveEdit = async () => {
    setBusy(true);
    try {
      const lines = editLines.map((l) => ({ fee_type_id: l.fee_type_id, name: l.name, due_date: l.due_date || null, amount: Number(l.amount) || 0, waiver: Number(l.waiver) || 0, paid: Number(l.paid) || 0, fee_group_id: l.fee_group_id }));
      await schoolApi.updateFeeInvoice(editInv.id, { organization_id: orgId, academic_year_id: editInv.academic_year_id, class_id: editInv.class_id, section_id: editInv.section_id, student_id: editInv.student_id, invoice_date: editInv.invoice_date, due_date: editInv.due_date, status: editInv.status, lines });
      toast.success('Invoice updated'); setEditInv(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="fees-invoices-page">
      <PageHeader icon={FileText} title="Fees Invoices" subtitle="All invoices for the selected academic year" />
      <Card className="p-3 flex flex-wrap gap-3 items-center">
        <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8 w-64" placeholder="Search invoice #, student or guardian..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} data-testid="inv-search" /></div>
        <Select value={filters.class_id || 'all'} onValueChange={(v) => setFilter({ class_id: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-48" data-testid="inv-filter-class"><SelectValue placeholder="Class" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All classes</SelectItem>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
        <Select value={filters.guardian_id || 'all'} onValueChange={(v) => setFilter({ guardian_id: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-56" data-testid="inv-filter-guardian"><SelectValue placeholder="Guardian" /></SelectTrigger>
          <SelectContent className="max-h-72"><SelectItem value="all">All guardians</SelectItem>{families.map((f) => <SelectItem key={f.id} value={f.id}>{f.name_en || f.name}</SelectItem>)}</SelectContent></Select>
        {filters.guardian_id && (
          <Button variant="outline" onClick={() => navigate(`/school/fees/statements?guardian_id=${filters.guardian_id}`)} data-testid="inv-view-statement"><Receipt className="w-4 h-4 mr-1" /> View Statement</Button>
        )}
        <Select value={filters.status || 'all'} onValueChange={(v) => setFilter({ status: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-40" data-testid="inv-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All status</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="partial">Partial</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent></Select>
      </Card>
      {loading ? <LoadingState /> : rows.length === 0 ? <EmptyState icon={FileText} title="No invoices" /> : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="p-3">Invoice #</th><th className="p-3">Student</th><th className="p-3">Guardian</th><th className="p-3">Class</th><th className="p-3">Date</th><th className="p-3 text-right">Total</th><th className="p-3 text-right">Paid</th><th className="p-3 text-right">Balance</th><th className="p-3">Status</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b hover:bg-muted/40" data-testid={`inv-row-${r.id}`}>
                  <td className="p-3 font-mono">{r.invoice_number}</td>
                  <td className="p-3">{r.student_name || '\u2014'}</td>
                  <td className="p-3" data-testid={`inv-guardian-${r.id}`}>{r.guardian_name || '\u2014'}</td>
                  <td className="p-3">{r.class_name || '\u2014'}</td>
                  <td className="p-3">{r.invoice_date}</td>
                  <td className="p-3 text-right">{money(r.total_subtotal)}</td>
                  <td className="p-3 text-right">{money(r.total_paid)}</td>
                  <td className="p-3 text-right">{money(r.balance)}</td>
                  <td className="p-3"><Badge className={STATUS_COLORS[r.status] || ''}>{r.status}</Badge>{r.is_posted && <Badge variant="outline" className="ml-1">Posted</Badge>}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setView(r)} data-testid={`inv-view-${r.id}`}><Eye className="w-4 h-4" /></Button>
                    {canEditInv && !r.is_posted && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`inv-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>}
                    {!r.is_posted && <Button size="icon" variant="ghost" className="h-8 w-8 text-blue-600" onClick={() => postInvoice(r)} title="Post to accounting" data-testid={`inv-post-${r.id}`}><CheckCircle2 className="w-4 h-4" /></Button>}
                    {r.is_posted && isSuper && <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-600" onClick={() => { setUnpostInv(r); setUnpostReason(''); }} title="Unpost (super admin)" data-testid={`inv-unpost-${r.id}`}><RotateCcw className="w-4 h-4" /></Button>}
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" onClick={() => openReceipts(r)} title="Register receipt" data-testid={`inv-receipt-${r.id}`}><Wallet className="w-4 h-4" /></Button>
                    {canDeleteInv && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(r)} disabled={r.is_posted} data-testid={`inv-del-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {total > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3" data-testid="inv-pagination">
          <div className="text-sm text-muted-foreground" data-testid="inv-pagination-counter">
            Showing <span className="font-medium text-foreground">{rangeStart}-{rangeEnd}</span> of <span className="font-medium text-foreground">{total}</span> invoices
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:inline">Per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => changePageSize(Number(v))}>
              <SelectTrigger className="w-20" data-testid="inv-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground" data-testid="inv-page-indicator">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={loading || page <= 1} data-testid="inv-prev-page">Previous</Button>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={loading || page >= totalPages} data-testid="inv-next-page">Next</Button>
          </div>
        </div>
      )}

      <Dialog open={!!view} onOpenChange={(o) => { if (!o) setView(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inv-view-dialog">
          <DialogHeader><DialogTitle>Invoice {view?.invoice_number}</DialogTitle></DialogHeader>
          {view && (
            <>
              <div ref={printRef} className="p-6 bg-white text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <div className="flex items-center gap-3 border-b pb-3 mb-3">
                  {currentOrg?.logo_url && <img src={currentOrg.logo_url} alt="logo" style={{ width: 48, height: 48, objectFit: 'contain' }} crossOrigin="anonymous" />}
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 18 }}>{currentOrg?.name || 'School'}</div>
                    <div style={{ fontSize: 12, color: '#555' }}>Fees Invoice</div>
                  </div>
                  <div style={{ marginLeft: 'auto', textAlign: 'right', fontSize: 12 }}>
                    <div><strong>{view.invoice_number}</strong></div>
                    <div>Date: {view.invoice_date}</div>
                    {view.due_date && <div>Due: {view.due_date}</div>}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', fontSize: 13, marginBottom: 12 }}>
                  <div>Student: <strong>{view.student_name}</strong><br />Admission #: {view.admission_no || '\u2014'}</div>
                  <div style={{ textAlign: 'right' }}>Class: {view.class_name} {view.section_name ? `/ ${view.section_name}` : ''}<br />Year: {view.academic_year_name}</div>
                </div>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead><tr style={{ borderBottom: '1px solid #ccc', textAlign: 'left' }}>
                    <th style={{ padding: 6 }}>Fee</th><th style={{ padding: 6 }}>Due</th><th style={{ padding: 6, textAlign: 'right' }}>Amount</th><th style={{ padding: 6, textAlign: 'right' }}>Waiver</th><th style={{ padding: 6, textAlign: 'right' }}>Sub total</th>
                  </tr></thead>
                  <tbody>{(view.lines || []).map((l, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: 6 }}>{l.name}</td><td style={{ padding: 6 }}>{l.due_date || '\u2014'}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>{money(l.amount)}</td><td style={{ padding: 6, textAlign: 'right' }}>{money(l.waiver)}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>{money(l.sub_total)}</td>
                    </tr>))}</tbody>
                </table>
                <div style={{ marginTop: 12, textAlign: 'right', fontSize: 13 }}>
                  <div>Total due: <strong>{money(view.total_subtotal)}</strong></div>
                  <div>Amount paid: {money(view.total_paid)}</div>
                  <div>Balance remaining: <strong>{money(view.balance)}</strong></div>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setView(null)}>Close</Button>
                <Button onClick={printPdf} data-testid="inv-print"><Printer className="w-4 h-4 mr-1" /> Print PDF</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Register Receipt dialog */}
      <Dialog open={!!receiptFor} onOpenChange={(o) => { if (!o) setReceiptFor(null); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="receipt-dialog">
          <DialogHeader><DialogTitle>Receipts - {receiptFor?.invoice_number}</DialogTitle></DialogHeader>
          {receiptFor && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground" data-testid="rcp-summary">Total due {money(receiptFor.total_subtotal)} | Paid {money(receiptFor.total_paid)} | Balance {money(receiptFor.balance)}</div>

              {payableLines.length === 0 ? (
                <div className="border rounded-md p-4 text-sm text-emerald-700 bg-emerald-50" data-testid="rcp-fully-paid">Invoice fully paid {'\u2014'} no outstanding balance.</div>
              ) : (
                <div className="space-y-3">
                  <div className="overflow-x-auto border rounded-md">
                    <table className="w-full text-sm" data-testid="rcp-lines-table">
                      <thead><tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="p-2 w-8"></th><th className="p-2">Fee</th><th className="p-2">Due date</th>
                        <th className="p-2 text-right">Amount</th><th className="p-2 text-right">Waiver</th><th className="p-2 text-right">Subtotal</th>
                        <th className="p-2 text-right">Paid</th><th className="p-2 text-right">Balance</th><th className="p-2 text-right">Allocated</th><th className="p-2">Coverage</th>
                      </tr></thead>
                      <tbody>
                        {payableLines.map((l) => {
                          const alloc = allocation.alloc[l.line_id] || 0;
                          const bal = lineBal(l);
                          const st = alloc <= 0 ? 'none' : (alloc >= bal ? 'full' : 'partial');
                          return (
                            <tr key={l.line_id} className="border-b" data-testid={`rcp-line-${l.line_id}`}>
                              <td className="p-2"><Checkbox checked={!!included[l.line_id]} onCheckedChange={(v) => setIncluded((p) => ({ ...p, [l.line_id]: !!v }))} data-testid={`rcp-line-check-${l.line_id}`} /></td>
                              <td className="p-2">{l.name}</td>
                              <td className="p-2">{l.due_date || '\u2014'}</td>
                              <td className="p-2 text-right">{money(l.amount)}</td>
                              <td className="p-2 text-right">{money(l.waiver)}</td>
                              <td className="p-2 text-right">{money(lineSub(l))}</td>
                              <td className="p-2 text-right">{money(l.paid)}</td>
                              <td className="p-2 text-right font-medium">{money(bal)}</td>
                              <td className="p-2 text-right font-semibold" data-testid={`rcp-line-alloc-${l.line_id}`}>{alloc > 0 ? money(alloc) : '\u2014'}</td>
                              <td className="p-2"><Badge className={ALLOC_STATUS[st]} data-testid={`rcp-line-status-${l.line_id}`}>{st === 'none' ? 'Not covered' : st === 'full' ? 'Full' : 'Partial'}</Badge></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="text-xs text-muted-foreground">Outstanding on this invoice: <strong>{money(totalOutstanding)} FCFA</strong>. Payment is applied to the earliest due date first; uncheck a line to skip it.</div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 border rounded-md p-3">
                    <div><Label>Payment date</Label><Input type="date" value={rForm.payment_date} onChange={(e) => setRForm({ ...rForm, payment_date: e.target.value })} data-testid="rcp-date" /></div>
                    <div><Label>Amount</Label><Input type="number" value={rForm.amount} onChange={(e) => setRForm({ ...rForm, amount: e.target.value })} data-testid="rcp-amount" /></div>
                    <div><Label>Method</Label>
                      <Select value={rForm.method} onValueChange={(v) => setRForm({ ...rForm, method: v })}><SelectTrigger data-testid="rcp-method"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank_transfer">Bank transfer</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent></Select></div>
                    <div><Label>Reference</Label><Input value={rForm.reference} onChange={(e) => setRForm({ ...rForm, reference: e.target.value })} data-testid="rcp-reference" /></div>
                    <div className="md:col-span-2"><Label>Notes</Label><Input value={rForm.notes} onChange={(e) => setRForm({ ...rForm, notes: e.target.value })} data-testid="rcp-notes" /></div>
                    <div className="col-span-full"><Label>Coverage description</Label>
                      <Textarea rows={2} value={rForm.description} onChange={(e) => { setDescTouched(true); setRForm({ ...rForm, description: e.target.value }); }} placeholder={'Auto-generated from allocation \u2014 you can edit it'} data-testid="rcp-description" /></div>
                    {allocation.unallocated > 0 && Number(rForm.amount) > 0 && (
                      <div className="col-span-full text-xs text-amber-600" data-testid="rcp-unallocated-warn">
                        {money(allocation.unallocated)} FCFA of the entered amount is not allocated to any checked line and will be rejected. Reduce the amount or include more lines.
                      </div>
                    )}
                    <div className="col-span-full flex items-center justify-between">
                      <span className="text-sm">Allocated total: <strong data-testid="rcp-allocated-total">{money(Number(rForm.amount || 0) - allocation.unallocated)} FCFA</strong></span>
                      <Button onClick={addReceipt} disabled={busy} data-testid="rcp-add"><Plus className="w-4 h-4 mr-1" /> Register Receipt</Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="max-h-64 overflow-y-auto">
                {receipts.length === 0 ? <p className="text-sm text-muted-foreground">No receipts yet.</p> : (
                  <table className="w-full text-sm"><thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="p-2">Receipt</th><th className="p-2">Date</th><th className="p-2">Method</th><th className="p-2">Covers</th><th className="p-2 text-right">Amount</th><th className="p-2"></th></tr></thead>
                    <tbody>{receipts.map((r) => (
                      <tr key={r.id} className="border-b" data-testid={`rcp-row-${r.id}`}>
                        <td className="p-2 font-mono text-xs">{r.receipt_number} {r.is_posted && <Badge variant="outline">Posted</Badge>}</td>
                        <td className="p-2">{r.payment_date}</td><td className="p-2">{METHOD_LABELS[r.method] || r.method}</td>
                        <td className="p-2 text-xs text-muted-foreground max-w-xs truncate" title={r.description}>{r.description || '\u2014'}</td>
                        <td className="p-2 text-right">{money(r.amount)}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setPrintReceipt(r)} title="Print receipt" data-testid={`rcp-print-${r.id}`}><Printer className="w-4 h-4" /></Button>
                          {r.is_posted && isSuper && <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-600" onClick={() => { setUnpostRcp(r); setUnpostRcpReason(''); }} title="Unpost receipt (super admin)" data-testid={`rcp-unpost-${r.id}`}><RotateCcw className="w-4 h-4" /></Button>}
                          {!r.is_posted && <Button size="sm" variant="outline" onClick={() => postReceipt(r)} data-testid={`rcp-post-${r.id}`}>Post</Button>}
                          {!r.is_posted && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => delReceipt(r)}><Trash2 className="w-4 h-4" /></Button>}
                        </td>
                      </tr>))}</tbody></table>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt print preview dialog */}
      <Dialog open={!!printReceipt} onOpenChange={(o) => { if (!o) setPrintReceipt(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="receipt-print-dialog">
          <DialogHeader><DialogTitle>Payment Receipt {printReceipt?.receipt_number}</DialogTitle></DialogHeader>
          {printReceipt && receiptFor && (() => {
            const lines = receiptFor.lines || [];
            const curTime = printReceipt.created_at || '';
            const prior = receipts
              .filter((r) => (r.created_at || '') < curTime)
              .sort((a, b) => ((a.created_at || '') < (b.created_at || '') ? -1 : 1));
            const previouslyPaid = prior.reduce((s, r) => s + Number(r.amount || 0), 0);
            const total = Number(receiptFor.total_subtotal || 0);
            const currentAmt = Number(printReceipt.amount || 0);
            const balanceAfter = Math.max(total - previouslyPaid - currentAmt, 0);
            const upTo = receipts.filter((r) => (r.created_at || '') <= curTime);
            const paidByLine = {};
            upTo.forEach((r) => (r.allocations || []).forEach((a) => { paidByLine[a.line_id] = (paidByLine[a.line_id] || 0) + Number(a.amount || 0); }));
            const curAlloc = Object.fromEntries((printReceipt.allocations || []).map((a) => [a.line_id, Number(a.amount || 0)]));
            const totalFees = lines.reduce((s, l) => s + Number(l.amount || 0), 0);
            const totalWaivers = lines.reduce((s, l) => s + Number(l.waiver || 0), 0);
            const th = { padding: '5px 6px' };
            const cell = { padding: '5px 6px' };
            return (
              <>
                <div ref={receiptPrintRef} className="p-6 bg-white text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderBottom: '2px solid #222', paddingBottom: 10, marginBottom: 12 }}>
                    {currentOrg?.logo_url && <img src={currentOrg.logo_url} alt="logo" style={{ width: 52, height: 52, objectFit: 'contain' }} crossOrigin="anonymous" />}
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 20 }}>{currentOrg?.name || 'School'}</div>
                      <div style={{ fontSize: 12, color: '#555', letterSpacing: 1, textTransform: 'uppercase' }}>Payment Receipt</div>
                    </div>
                    <div style={{ marginLeft: 'auto', textAlign: 'right', fontSize: 12 }}>
                      <div style={{ fontSize: 15, fontWeight: 700 }}>{printReceipt.receipt_number}</div>
                      <div>Date: {printReceipt.payment_date}</div>
                      <div>Invoice: <strong>{receiptFor.invoice_number}</strong></div>
                    </div>
                  </div>

                  {/* Prominent student header */}
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1 }}>{receiptFor.student_name}</div>
                    <div style={{ fontSize: 13, color: '#334155', marginTop: 2 }}>Guardian: <strong>{receiptFor.guardian_name || '\u2014'}</strong></div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 24px', fontSize: 13, marginTop: 8 }}>
                      <div>Admission #: <strong>{receiptFor.admission_no || '\u2014'}</strong></div>
                      <div>Academic Year: <strong>{receiptFor.academic_year_name || '\u2014'}</strong></div>
                      <div>Class: <strong>{receiptFor.class_name || '\u2014'}</strong></div>
                      <div>Section: <strong>{receiptFor.section_name || '\u2014'}</strong></div>
                    </div>
                  </div>

                  {/* Fee lines with status + allocation */}
                  <div style={{ fontSize: 13, fontWeight: 700, margin: '4px 0 6px' }}>Fee Lines</div>
                  <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                    <thead><tr style={{ borderBottom: '1px solid #ccc', textAlign: 'left', color: '#555' }}>
                      <th style={th}>Fee</th><th style={th}>Due</th>
                      <th style={{ ...th, textAlign: 'right' }}>Amount</th><th style={{ ...th, textAlign: 'right' }}>Waiver</th>
                      <th style={{ ...th, textAlign: 'right' }}>Subtotal</th><th style={{ ...th, textAlign: 'right' }}>This payment</th>
                      <th style={{ ...th, textAlign: 'right' }}>Paid to date</th><th style={{ ...th, textAlign: 'right' }}>Balance</th>
                      <th style={{ ...th, textAlign: 'center' }}>Status</th>
                    </tr></thead>
                    <tbody>{lines.map((l, i) => {
                      const sub = Number(l.sub_total != null ? l.sub_total : (Number(l.amount || 0) - Number(l.waiver || 0)));
                      const paid = Number(paidByLine[l.line_id] || 0);
                      const rem = Math.max(sub - paid, 0);
                      const status = paid <= 0 ? 'Unpaid' : (paid >= sub - 0.01 ? 'Paid' : 'Partial');
                      const bg = status === 'Paid' ? '#dcfce7' : status === 'Partial' ? '#ffedd5' : 'transparent';
                      const badge = status === 'Paid' ? '#166534' : status === 'Partial' ? '#9a3412' : '#64748b';
                      const applied = curAlloc[l.line_id] || 0;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #eee', background: bg }}>
                          <td style={cell}>{l.name}</td>
                          <td style={cell}>{l.due_date || '\u2014'}</td>
                          <td style={{ ...cell, textAlign: 'right' }}>{money(l.amount)}</td>
                          <td style={{ ...cell, textAlign: 'right' }}>{money(l.waiver)}</td>
                          <td style={{ ...cell, textAlign: 'right' }}>{money(sub)}</td>
                          <td style={{ ...cell, textAlign: 'right', fontWeight: applied ? 700 : 400, color: applied ? '#166534' : '#94a3b8' }}>{applied ? money(applied) : '\u2014'}</td>
                          <td style={{ ...cell, textAlign: 'right' }}>{money(paid)}</td>
                          <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{money(rem)}{status === 'Partial' ? <span style={{ color: '#9a3412', fontWeight: 400 }}> left</span> : null}</td>
                          <td style={{ ...cell, textAlign: 'center' }}><span style={{ color: badge, fontWeight: 700 }}>{status}</span></td>
                        </tr>);
                    })}</tbody>
                  </table>

                  {/* Payment history */}
                  <div style={{ fontSize: 13, fontWeight: 700, margin: '16px 0 6px' }}>Previous Payments</div>
                  {prior.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#777' }}>No previous payments on this invoice.</div>
                  ) : (
                    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                      <thead><tr style={{ borderBottom: '1px solid #ccc', textAlign: 'left', color: '#555' }}>
                        <th style={th}>Receipt</th><th style={th}>Date</th><th style={th}>Method</th><th style={{ ...th, textAlign: 'right' }}>Amount</th>
                      </tr></thead>
                      <tbody>{prior.map((r) => (
                        <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ ...cell, fontFamily: 'monospace' }}>{r.receipt_number}</td>
                          <td style={cell}>{r.payment_date}</td>
                          <td style={cell}>{METHOD_LABELS[r.method] || r.method}</td>
                          <td style={{ ...cell, textAlign: 'right' }}>{money(r.amount)}</td>
                        </tr>))}</tbody>
                    </table>
                  )}

                  <div style={{ fontSize: 13, fontWeight: 700, margin: '14px 0 6px', color: '#166534' }}>Current Payment</div>
                  <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', background: '#f0fdf4' }}>
                    <tbody>
                      <tr>
                        <td style={{ ...cell, fontFamily: 'monospace' }}>{printReceipt.receipt_number}</td>
                        <td style={cell}>{printReceipt.payment_date}</td>
                        <td style={cell}>{METHOD_LABELS[printReceipt.method] || printReceipt.method}{printReceipt.reference ? ` \u00b7 Ref ${printReceipt.reference}` : ''}</td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>{money(currentAmt)} FCFA</td>
                      </tr>
                    </tbody>
                  </table>

                  <div style={{ marginTop: 10, marginLeft: 'auto', width: 320, fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span>Total invoiced</span><strong>{money(total)}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span>Total previously paid</span><strong>{money(previouslyPaid)}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}><span>Current payment</span><strong style={{ color: '#166534' }}>{money(currentAmt)}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderTop: '1px solid #cbd5e1', marginTop: 2 }}><span>Balance remaining after this receipt</span><strong>{money(balanceAfter)}</strong></div>
                  </div>

                  <div style={{ marginTop: 12, fontSize: 12, fontStyle: 'italic', color: '#333' }}>
                    Amount in words: <strong>{amountToWords(currentAmt, 'XOF')}</strong>
                  </div>
                  {printReceipt.description ? <div style={{ marginTop: 8, fontSize: 12, color: '#333' }}>{printReceipt.description}</div> : null}
                  {printReceipt.notes ? <div style={{ marginTop: 4, fontSize: 12, color: '#555' }}>Notes: {printReceipt.notes}</div> : null}

                  {/* Summary box */}
                  <div style={{ marginTop: 16, border: '2px solid #222', borderRadius: 8, padding: '12px 14px', background: '#f8fafc' }}>
                    <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Invoice Summary</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', fontSize: 13 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total Fees</span><strong>{money(totalFees)}</strong></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total Waivers</span><strong>{money(totalWaivers)}</strong></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Net Due</span><strong>{money(total)}</strong></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total Paid (all receipts)</span><strong style={{ color: '#166534' }}>{money(receiptFor.total_paid)}</strong></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gridColumn: '1 / -1', borderTop: '1px solid #cbd5e1', paddingTop: 6, marginTop: 2, fontSize: 15 }}><span style={{ fontWeight: 700 }}>Outstanding Balance</span><strong style={{ color: Number(receiptFor.balance) > 0 ? '#9a3412' : '#166534' }}>{money(receiptFor.balance)} FCFA</strong></div>
                    </div>
                  </div>

                  <div style={{ marginTop: 36, display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#555' }}>
                    <div>Received by: ____________________</div>
                    <div>Signature &amp; Stamp: ____________________</div>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setPrintReceipt(null)}>Close</Button>
                  <Button onClick={printReceiptNative} data-testid="rcp-print-pdf"><Printer className="w-4 h-4 mr-1" /> Print</Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Edit invoice dialog */}
      <Dialog open={!!editInv} onOpenChange={(o) => { if (!o) setEditInv(null); }}>
        <DialogContent className="max-w-2xl" data-testid="edit-dialog">
          <DialogHeader><DialogTitle>Edit {editInv?.invoice_number}</DialogTitle></DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm"><thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="p-2">Fee</th><th className="p-2 text-right">Amount</th><th className="p-2 text-right">Waiver</th><th className="p-2 text-right">Paid</th></tr></thead>
              <tbody>{editLines.map((l, i) => (
                <tr key={i} className="border-b">
                  <td className="p-2">{l.name}</td>
                  <td className="p-2"><Input type="number" className="w-28 text-right ml-auto" value={l.amount} onChange={(e) => setEL(i, 'amount', e.target.value)} data-testid={`edit-amount-${i}`} /></td>
                  <td className="p-2"><Input type="number" className="w-24 text-right ml-auto" value={l.waiver} onChange={(e) => setEL(i, 'waiver', e.target.value)} /></td>
                  <td className="p-2"><Input type="number" className="w-24 text-right ml-auto" value={l.paid} onChange={(e) => setEL(i, 'paid', e.target.value)} /></td>
                </tr>))}</tbody></table>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setEditInv(null)}>Cancel</Button><Button onClick={saveEdit} disabled={busy} data-testid="edit-save">Save</Button></div>
        </DialogContent>
      </Dialog>

      {/* Unpost invoice dialog (super admin only) */}
      <Dialog open={!!unpostInv} onOpenChange={(o) => { if (!o) { setUnpostInv(null); setUnpostReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="unpost-dialog">
          <DialogHeader><DialogTitle>Unpost invoice {unpostInv?.invoice_number}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              This will void the accounting journal entries created when this invoice was posted and return it to <strong>Draft</strong> for editing. This action is logged in the audit trail.
            </p>
            <div>
              <Label htmlFor="unpost-reason">Reason <span className="text-red-600">*</span></Label>
              <Textarea id="unpost-reason" rows={3} value={unpostReason} onChange={(e) => setUnpostReason(e.target.value)} placeholder="Explain why this invoice is being unposted" data-testid="unpost-reason" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setUnpostInv(null); setUnpostReason(''); }} data-testid="unpost-cancel">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={doUnpost} disabled={busy || !unpostReason.trim()} data-testid="unpost-confirm"><RotateCcw className="w-4 h-4 mr-1" /> Confirm Unpost</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unpost receipt dialog (super admin only) */}
      <Dialog open={!!unpostRcp} onOpenChange={(o) => { if (!o) { setUnpostRcp(null); setUnpostRcpReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="unpost-receipt-dialog">
          <DialogHeader><DialogTitle>Unpost receipt {unpostRcp?.receipt_number}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              This will reverse the receipt's journal entry (an offsetting entry is posted), remove its payment from the invoice so the balance reverts, and set the receipt back to <strong>Draft</strong> so it can be edited or deleted. This action is logged in the audit trail.
            </p>
            <div>
              <Label htmlFor="unpost-receipt-reason">Reason <span className="text-red-600">*</span></Label>
              <Textarea id="unpost-receipt-reason" rows={3} value={unpostRcpReason} onChange={(e) => setUnpostRcpReason(e.target.value)} placeholder="Explain why this receipt is being unposted" data-testid="unpost-receipt-reason" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setUnpostRcp(null); setUnpostRcpReason(''); }} data-testid="unpost-receipt-cancel">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={doUnpostReceipt} disabled={busy || !unpostRcpReason.trim()} data-testid="unpost-receipt-confirm"><RotateCcw className="w-4 h-4 mr-1" /> Confirm Unpost</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
